import { useEffect, useState } from "react";
import { ArrowRight, Check, HelpCircle } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import {
  FAQS,
  FEATURES,
  HIGHLIGHTS,
  ROLES,
  STAGES,
  STATS,
  STEPS,
} from "../data/landingPageContent";
import {
  CustomerMock,
  DashboardMock,
  HeroMock,
  TimelineMock,
} from "../components/landingpage/illustrations";
import {
  BRAND,
  BRAND_LIGHT,
  Bullet,
  ButtonLink,
  Eyebrow,
  ICONS,
  INK,
  INK_2,
  IconTile,
  MIST,
  Pattern,
  Reveal,
  SectionHeading,
  TINT,
  WRAP,
} from "../components/landingpage/ui";
import Navbar from "../components/landingpage/Navbar";
import Footer from "../components/landingpage/Footer";
import GetStartedBanner from "../components/landingpage/GetStartedBanner";
import InAction from "../components/landingpage/InAction";

/* Safe icon lookup: falls back to a neutral icon if a data entry names an
   icon that is not registered in ICONS, instead of crashing the page. */
const getIcon = (name) => ICONS[name] ?? HelpCircle;

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const { hash } = useLocation();

  // Smooth scrolling for the "#section" links while this page is open.
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.scrollBehavior;
    root.style.scrollBehavior = "smooth";
    return () => {
      root.style.scrollBehavior = previous;
    };
  }, []);

  // Give the navbar a shadow once the page is scrolled.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Arriving from another page (for example the pricing page) with "/#section":
  // scroll to that section once the page has rendered.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  return (
    <div className="min-h-screen overflow-x-clip bg-[#EAF0F2] text-[#061A21]">
      <style>{`
        @keyframes izFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-9px); } }
        @keyframes izWave { 0%,100% { transform: scaleY(0.55); } 50% { transform: scaleY(1); } }
        .iz-float { animation: izFloat 5s ease-in-out infinite; }
        .iz-wave { transform-origin: center; animation: izWave 1.3s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .iz-float, .iz-wave { animation: none !important; } }
      `}</style>

      <Navbar scrolled={scrolled} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <main>
        {/* ═════════════ HERO (dark, plain) ═════════════ */}
        <section id="top" className="relative overflow-hidden" style={{ background: INK }}>
          <div className={`${WRAP} relative pt-16 text-center sm:pt-20`}>
            <Reveal delay={80}>
              <h1 className="max-w-5xl mx-auto text-[40px] font-extrabold leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-[76px]">
                Your entire sales process, <span style={{ color: BRAND_LIGHT }}>in one powerful place.</span>
              </h1>
            </Reveal>

            <Reveal delay={160}>
              <p className="max-w-2xl mx-auto mt-7 text-[17px] sm:text-[19px] leading-relaxed text-slate-300">
                IZSales helps your team manage companies, move deals through the pipeline, log every call, email
                and meeting, and never miss a follow-up.
              </p>
            </Reveal>

            <Reveal delay={240}>
              <div className="flex flex-col items-center justify-center gap-3 mt-10 sm:flex-row">
                <ButtonLink href="#how-it-works" variant="ghostLight" className="!h-14 !px-9 !text-[16px]">
                  See how it works
                </ButtonLink>
              </div>
            </Reveal>

            <Reveal delay={320}>
              <ul className="flex flex-wrap justify-center mt-9 gap-x-8 gap-y-3">
                {["Visual sales pipeline", "Full interaction history", "Tasks & follow-ups"].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-[14px] font-medium text-slate-300">
                    <span className="flex items-center justify-center w-5 h-5 rounded-md bg-cyan-400/15 text-cyan-300">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          {/* Large realistic app screen */}
          <div className={`${WRAP} relative mt-16 pb-24 sm:mt-20 sm:pb-28`}>
            <Reveal delay={200}>
              <div className="relative">
                <HeroMock />
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═════════════ STATS (floating card, half on the hero and half on the next section) ═════════════ */}
        <section className="relative z-10">
          <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1/2" style={{ background: INK }} />
          <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1/2" style={{ background: TINT }} />
          <div className={`${WRAP} relative`}>
            <Reveal>
              <div
                className="grid grid-cols-2 px-4 py-10 gap-y-8 rounded-2xl lg:grid-cols-4 lg:px-6"
                style={{ background: BRAND, boxShadow: "0 28px 60px -24px rgba(6,26,33,0.55)" }}
              >
                {STATS.map((s, i) => (
                  <div
                    key={s.value + s.label}
                    className={`px-4 text-center lg:px-8 ${i > 0 ? "lg:border-l lg:border-white/25" : ""}`}
                  >
                    <div className="text-5xl font-extrabold text-white">{s.value}</div>
                    <div className="max-w-[210px] mx-auto mt-2 text-[14.5px] leading-snug text-white/90">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═════════════ ABOUT (teal tint) ═════════════ */}
        <section id="about" className="relative py-24 overflow-hidden scroll-mt-24" style={{ background: TINT }}>
          <div className={`${WRAP} relative grid items-center gap-14 lg:grid-cols-2`}>
            <Reveal>
              <Eyebrow>What is IZSales</Eyebrow>
              <h2 className="mt-5 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-[46px] lg:leading-[1.1]">
                One CRM for your whole sales team.
              </h2>
              <p className="mt-5 text-[17px] leading-relaxed text-slate-700">
                IZSales is a web-based customer relationship management (CRM) system. It brings your companies,
                deals, tasks and conversations together, so everyone works from the same up-to-date picture instead
                of chasing information across spreadsheets, inboxes and notebooks.
              </p>
              <div className="mt-8">
                <ButtonLink href="#features" variant="outline">
                  Explore the features
                </ButtonLink>
              </div>
            </Reveal>

            <div className="space-y-4">
              {HIGHLIGHTS.map(({ icon, title, text }, i) => {
                const Icon = getIcon(icon);
                return (
                  <Reveal key={title} delay={i * 110}>
                    <div className="flex items-start gap-5 p-6 transition-all bg-white border shadow-sm rounded-xl border-slate-300 hover:shadow-lg hover:-translate-y-0.5">
                      <IconTile icon={Icon} />
                      <div>
                        <h3 className="text-[18px] font-bold">{title}</h3>
                        <p className="mt-1.5 text-[15px] leading-relaxed text-slate-600">{text}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═════════════ PIPELINE (dark) ═════════════ */}
        <section id="pipeline" className="relative py-24 overflow-hidden scroll-mt-24" style={{ background: INK }}>
          <div className={`${WRAP} relative`}>
            <div className="grid items-center gap-14 lg:grid-cols-2">
              <Reveal>
                <Eyebrow dark>Sales pipeline</Eyebrow>
                <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-[46px] lg:leading-[1.1]">
                  Take every opportunity from prospect to customer.
                </h2>
                <p className="mt-5 text-[17px] leading-relaxed text-slate-300">
                  Every company sits on a visual board. Managers verify new prospects, stalled leads are highlighted,
                  and deals are closed as won or lost with the final amount.
                </p>
                <ul className="mt-8 space-y-4">
                  <Bullet dark>Managers record a Qualified or Not Qualified decision with a research note.</Bullet>
                  <Bullet dark>Companies with no recent activity in the middle stages are highlighted.</Bullet>
                  <Bullet dark>Add deals, then close them as won or lost with the amount and a note.</Bullet>
                  <Bullet dark>Won companies move on to Customer.</Bullet>
                </ul>
              </Reveal>
              <Reveal delay={150}>
                <CustomerMock />
              </Reveal>
            </div>

            <Reveal delay={100}>
              <div className="mt-16 border p-7 rounded-2xl border-white/10 sm:p-9" style={{ background: INK_2 }}>
                <h3 className="text-center text-[19px] font-bold text-white">
                  Eight stages from prospect to customer
                </h3>
                <ol className="flex flex-wrap items-center justify-center mt-7 gap-y-3">
                  {STAGES.map((stage, i) => (
                    <li key={stage.name} className="flex items-center">
                      <span className="inline-flex items-center gap-2 px-4 py-2 text-[13.5px] font-semibold text-white border rounded-lg border-white/15 bg-white/5">
                        <span
                          className="flex items-center justify-center w-5 h-5 text-[11px] font-bold rounded text-[#061A21]"
                          style={{ background: BRAND_LIGHT }}
                        >
                          {i + 1}
                        </span>
                        {stage.name}
                      </span>
                      {i < STAGES.length - 1 && <ArrowRight size={16} className="mx-2 text-cyan-400/60" />}
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </div>
        </section>

         {/* ═════════════ FEATURES (the only checked section) ═════════════ */}
        <section id="features" className="relative py-24 overflow-hidden scroll-mt-24" style={{ background: MIST }}>
          <Pattern type="grid" size={48} />
          <div className={`${WRAP} relative`}>
            <SectionHeading
              center={false}
              eyebrow="Features"
              title="Everything your sales team needs."
              text="From the first call to the closed deal, IZSales keeps your work organised and your team aligned."
            />
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {FEATURES.map((f, i) => {
                const Icon = getIcon(f.icon);
                return (
                  <Reveal key={f.title} delay={(i % 3) * 90}>
                    <div className="flex flex-col h-full p-7 transition-all duration-300 bg-white border shadow-sm group rounded-xl border-slate-300 hover:shadow-xl hover:-translate-y-1 hover:border-[#009DB9]">
                      <div className="mb-5">
                        <IconTile icon={Icon} />
                      </div>
                      <h3 className="text-[18px] font-bold">{f.title}</h3>
                      <p className="mt-2 text-[14.5px] leading-relaxed text-slate-600">{f.text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═════════════ DASHBOARD (grey-blue) ═════════════ */}
        <section id="dashboard" className="relative py-24 overflow-hidden scroll-mt-24 border-t border-slate-200/80 shadow-[inset_0_8px_24px_-12px_rgba(6,26,33,0.12)]" style={{ background: "#F4F8FA" }}>
          <div className={`${WRAP} relative grid items-center gap-14 lg:grid-cols-12`}>
            <Reveal className="lg:col-span-7">
              <DashboardMock />
            </Reveal>
            <Reveal delay={150} className="lg:col-span-5">
              <Eyebrow>Dashboard</Eyebrow>
              <h2 className="mt-5 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-[44px] lg:leading-[1.1]">
                Know Where Your Team Stand After Sign In
              </h2>
              <p className="mt-5 text-[17px] leading-relaxed text-slate-700">
                Your dashboard turns daily activity into numbers you can act on, without building a single report.
              </p>
              <ul className="mt-8 space-y-4">
                <Bullet>Revenue from won deals, active deals, tasks due today and total companies.</Bullet>
                <Bullet>Pipeline value and deals broken down by stage.</Bullet>
                <Bullet>Your mix of calls, emails, meetings, notes and field work.</Bullet>
                <Bullet>Company status across the whole pipeline.</Bullet>
              </ul>
            </Reveal>
          </div>
        </section>

        {/* ═════════════ INTERACTIONS (white) ═════════════ */}
        <section id="interactions" className="relative py-24 overflow-hidden bg-white scroll-mt-24">
          <div className={`${WRAP} relative grid items-center gap-14 lg:grid-cols-12`}>
            <Reveal className="lg:col-span-5">
              <Eyebrow>Interactions</Eyebrow>
              <h2 className="mt-5 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-[44px] lg:leading-[1.1]">
                Every conversation, recorded in one timeline.
              </h2>
              <p className="mt-5 text-[17px] leading-relaxed text-slate-700">
                Log what happened the moment it happens. Anyone on the team can open a company and see the whole
                story, from the first call to the latest note.
              </p>
              <ul className="mt-8 space-y-4">
                <Bullet>Log calls, emails, meetings, notes and field work against each company.</Bullet>
                <Bullet>
                  Notes can be Research Mapping, Follow-up or Office Visit, and edited notes are clearly marked.
                </Bullet>
                <Bullet>Send emails with To, CC, a formatted body and a PDF, and keep them in the history.</Bullet>
                <Bullet>Record a call and play it back from the company history.</Bullet>
              </ul>
            </Reveal>
            <Reveal delay={150} className="lg:col-span-7">
              <TimelineMock />
            </Reveal>
          </div>
        </section>

        {/* ═════════════ GET STARTED BANNER ═════════════ */}
        <GetStartedBanner />

        {/* ═════════════ IZSALES IN ACTION (teal tint) ═════════════ */}
        <InAction />

        {/* ═════════════ HOW IT WORKS (dark panel cards on ink) ═════════════ */}
        <section id="how-it-works" className="relative py-24 overflow-hidden scroll-mt-24" style={{ background: INK }}>
          <div className={`${WRAP} relative`}>
            <SectionHeading
              dark
              eyebrow="How it works"
              title="From prospect to customer in six steps."
              text="A simple flow your whole team can follow from day one."
            />
            <div className="relative grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {STEPS.map((step, i) => {
                const Icon = getIcon(step.icon);
                return (
                  <Reveal key={step.title} delay={(i % 3) * 110}>
                    <div
                      className="relative h-full text-center transition-all border p-7 rounded-2xl border-white/10 hover:-translate-y-1 hover:border-cyan-400/40"
                      style={{ background: INK_2 }}
                    >
                      <div
                        className="relative flex items-center justify-center mx-auto mb-5 text-white w-14 h-14 rounded-xl"
                        style={{ background: BRAND }}
                      >
                        <Icon size={24} />
                        <span className="absolute flex items-center justify-center w-6 h-6 text-[11px] font-extrabold bg-white border-2 rounded-md -top-2 -right-2 text-[#007a90] border-[#009DB9]">
                          {i + 1}
                        </span>
                      </div>
                      <h3 className="text-[18px] font-bold text-white">{step.title}</h3>
                      <p className="mt-2 text-[14.5px] leading-relaxed text-slate-300">{step.text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

       

        {/* ═════════════ ROLES (dark) ═════════════ */}
        {/* <section id="roles" className="relative py-24 overflow-hidden scroll-mt-24" style={{ background: INK }}>
          <div className={`${WRAP} relative`}>
            <SectionHeading
              dark
              eyebrow="Who it's for"
              title="Built for every role on your team."
              text="Everyone sees the tools that fit their job."
            />
            <div className="grid gap-6 md:grid-cols-3">
              {ROLES.map((role, i) => (
                <Reveal key={role.title} delay={i * 110}>
                  <div
                    className="flex flex-col h-full p-8 transition-colors border rounded-2xl border-white/10 hover:border-cyan-400/40"
                    style={{ background: INK_2 }}
                  >
                    <h3 className="text-[22px] font-bold text-white">{role.title}</h3>
                    <p className="mt-2 mb-6 text-[15px] leading-relaxed text-slate-300">{role.text}</p>
                    <ul className="mt-auto space-y-3.5">
                      {role.points.map((p) => (
                        <Bullet key={p} dark>
                          {p}
                        </Bullet>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section> */}

        {/* ═════════════ FAQ (teal tint) ═════════════ */}
        <section id="faq" className="relative py-24 overflow-hidden scroll-mt-24" style={{ background: TINT }}>
          <div className={`${WRAP} relative max-w-4xl`}>
            <SectionHeading title="FAQ" center={false} />
            <div className="space-y-3">
              {FAQS.map((item, i) => {
                const open = openFaq === i;
                return (
                  <Reveal key={item.q} delay={i * 60}>
                    <div
                      className={`overflow-hidden transition-all bg-white border rounded-xl ${
                        open ? "border-[#009DB9] shadow-lg shadow-cyan-900/10" : "border-slate-300"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setOpenFaq(open ? -1 : i)}
                        aria-expanded={open}
                        className="flex items-center justify-between w-full gap-4 px-6 py-5 text-left"
                      >
                        <span className="text-[16.5px] font-semibold">{item.q}</span>
                        <ChevronDownIcon
                          open={open}
                          className={`flex-shrink-0 text-[#009DB9] transition-transform duration-300 ${
                            open ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      <div
                        className={`grid transition-all duration-300 ease-out ${
                          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div className="px-6 pb-6">
                            <p className="text-[15px] leading-relaxed text-slate-600">{item.a}</p>
                            {item.link && (
                              <Link
                                to={item.link.to}
                                className="inline-flex items-center gap-1.5 mt-3 text-[15px] font-semibold text-[#007a90] hover:underline"
                              >
                                {item.link.label} <ArrowRight size={15} />
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═════════════ FINAL CTA (solid brand) ═════════════ */}
        <section className="relative overflow-hidden" style={{ background: BRAND }}>
          <Reveal>
            <div className={`${WRAP} relative py-20 text-center sm:py-24`}>
              <h2 className="max-w-3xl mx-auto text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl lg:leading-[1.1]">
                Ready to get your sales organised?
              </h2>
              <p className="max-w-xl mx-auto mt-5 text-[17px] leading-relaxed text-white/90">
                Log in to IZSales to manage your companies, pipeline, tasks and conversations in one place.
              </p>
              <div className="flex flex-col items-center justify-center gap-3 mt-9 sm:flex-row">
                <ButtonLink href="#features" variant="ghostDark" className="!h-14 !px-9 !text-[16px]">
                  Explore features
                </ButtonLink>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* Small local helper so the FAQ chevron stays inside this page file. */
function ChevronDownIcon({ open, className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      width={20}
      height={20}
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}